const text = (value) => String(value ?? '').trim();

const assetFromCase = (link, caseInfo = {}) => {
  const id = String(link?.object_id ?? '');
  const type = text(link?.asset_type).toLowerCase();
  if (!id) return null;

  // Newer case payloads may include the same generic asset snapshots returned
  // by the creditor-linking endpoint.  Check those first so every Schedule A/B
  // asset type (including business and farm property) gets the same fallback
  // behavior as vehicles and real property.
  const linkedSnapshot = (caseInfo.linkable_assets || []).find(asset => (
    text(asset?.asset_type).toLowerCase() === type
    && String(asset?.object_id ?? asset?.id ?? '') === id
  ));
  if (linkedSnapshot) return linkedSnapshot.asset || linkedSnapshot;

  if (type === 'vehicle') {
    return (caseInfo.vehicles || []).find(asset => String(asset.id) === id) || null;
  }
  if (type === 'realproperty') {
    const properties = caseInfo.real_estates || caseInfo.real_properties || [];
    return properties.find(asset => String(asset.id) === id) || null;
  }
  return null;
};

const numericValue = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export function linkedAssetValue(link, caseInfo = {}) {
  const asset = link?.asset || assetFromCase(link, caseInfo) || {};
  const candidates = [
    link?.value_total,
    link?.value,
    link?.amount,
    link?.value_ownership_portion,
    asset.value_total,
    asset.value,
    asset.amount,
    asset.value_ownership_portion,
    asset.market_value,
  ];

  for (const candidate of candidates) {
    const value = numericValue(candidate);
    if (value !== null) return value;
  }
  return null;
}

export function totalLinkedCollateralValue(creditor, caseInfo = {}) {
  const seen = new Set();
  let foundValue = false;
  let total = 0;

  (creditor?.asset_links || []).forEach((link, index) => {
    const assetType = text(link?.asset_type).toLowerCase();
    const objectId = text(link?.object_id);
    const linkId = text(link?.id);
    const key = assetType && objectId
      ? `${assetType}:${objectId}`
      : linkId
        ? `link:${linkId}`
        : `index:${index}`;
    if (seen.has(key)) return;
    seen.add(key);

    const value = linkedAssetValue(link, caseInfo);
    if (value === null) return;
    foundValue = true;
    total += value;
  });

  return foundValue ? total : null;
}

export function linkedAssetDescription(link, caseInfo = {}) {
  const suppliedLabel = text(link?.asset_label);
  if (suppliedLabel && suppliedLabel.toLowerCase() !== 'unavailable asset') {
    return suppliedLabel;
  }

  const asset = assetFromCase(link, caseInfo);
  const type = text(link?.asset_type).toLowerCase();
  if (type === 'vehicle' && asset) {
    return [asset.year, asset.make, asset.model].map(text).filter(Boolean).join(' ');
  }
  if (type === 'realproperty' && asset) {
    return [asset.street1, asset.city].map(text).filter(Boolean).join(', ');
  }

  const genericAssetLabel = text(asset?.asset_label)
    || text(asset?.label)
    || text(asset?.description);
  if (genericAssetLabel && genericAssetLabel.toLowerCase() !== 'unavailable asset') {
    return genericAssetLabel;
  }

  return text(link?.asset_type_label)
    || text(link?.description)
    || text(link?.asset_type).replace(/_/g, ' ');
}

export function statementOfIntentCollateralDescription(creditor, caseInfo = {}) {
  const seen = new Set();
  const descriptions = (creditor?.asset_links || [])
    .map(link => linkedAssetDescription(link, caseInfo))
    .filter(description => {
      if (!description) return false;
      const key = description.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return descriptions.join('; ') || text(creditor?.lien_description);
}

export function normalizeStatementOfIntentTreatment(value) {
  const normalized = text(value).toLowerCase();
  if (normalized === 'retain') return 'redeem';
  if (normalized === 'reaffirmation') return 'reaffirm';
  return normalized;
}

export function statementOfIntentSecuredDebts(debts = []) {
  return (debts || []).filter(debt => {
    const treatment = normalizeStatementOfIntentTreatment(
      debt?.statement_of_intent_secured_treatment,
    );
    const hasExplicitIntent = [
      'surrender',
      'redeem',
      'reaffirm',
      'other',
    ].includes(treatment);

    // An explicit Form 108 selection is authoritative. This prevents imported
    // or legacy non-auto collateral from disappearing merely because its debt
    // type or additional-notice flag was saved differently.
    if (hasExplicitIntent) return true;

    const additionalNotice = debt?.additional_notified === true
      || text(debt?.additional_notified).toLowerCase() === 'true';
    return text(debt?.debt_type).toLowerCase() === 'secured' && !additionalNotice;
  });
}
